<script>
  import { t, setLocale, getLocale } from '../i18n/index.js';
  import { showScreen } from '../stores/screen.js';
  import { session, connectionStatus, clearPairingInfo } from '../stores/session.js';
  import { paperSize, executionProvider } from '../stores/settings.js';
  import { stopCamera } from '../camera.js';
  import { stopSessionHealthCheck } from '../lib/session-health.js';
  import { reloadDocAlignerModel, warmupDocAligner, getInferenceStats } from '../docaligner-inference.js';
  import { WEB_VERSION } from '../main.js';

  let inferenceStats = $state({ avgTime: 0, count: 0 });
  let changingEngine = $state(false);

  // Load inference stats on mount
  $effect(() => {
    inferenceStats = getInferenceStats();
  });

  function goBack() {
    showScreen('camera');
  }

  async function handleLanguageChange(lang) {
    setLocale(lang);
  }

  function handlePaperSizeChange(size) {
    paperSize.set(size);
  }

  async function handleEngineChange(provider) {
    if (provider === $executionProvider || changingEngine) return;

    changingEngine = true;
    executionProvider.set(provider);

    try {
      await reloadDocAlignerModel(provider);
      await warmupDocAligner();
      inferenceStats = getInferenceStats();
    } catch (e) {
      console.error('Failed to reload model:', e);
    } finally {
      changingEngine = false;
    }
  }

  function handleDisconnect() {
    if (confirm(t('settings.disconnectConfirm'))) {
      stopCamera();
      stopSessionHealthCheck();
      clearPairingInfo();
      showScreen('pairing');
    }
  }

  function handleReport() {
    // TODO: Implement report dialog
  }
</script>

<div class="screen flex flex-col h-full">
  <header class="p-4 flex items-center gap-4 border-b border-gray-700">
    <button onclick={goBack} class="p-2 -ml-2 rounded-full hover:bg-gray-800" aria-label="Go back">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <h1 class="text-xl font-semibold">{t('settings.title')}</h1>
  </header>

  <main class="flex-1 p-4 space-y-6 overflow-y-auto">
    <!-- Connection status -->
    <div class="bg-gray-800 rounded-lg p-4">
      <h3 class="font-medium mb-2">{t('settings.connectionStatus')}</h3>
      <div class="flex items-center gap-2">
        <span class="w-3 h-3 rounded-full {$connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'}"></span>
        <span class="text-gray-300">
          {$connectionStatus === 'connected' ? t('settings.connected') : t('settings.notConnected')}
        </span>
      </div>
      {#if $session}
        <p class="text-sm text-gray-500 mt-2">Device ID: {$session.device_id}</p>
      {/if}
    </div>

    <!-- Language -->
    <div class="bg-gray-800 rounded-lg p-4">
      <h3 class="font-medium mb-2">{t('settings.language')}</h3>
      <div class="flex gap-2">
        <button
          onclick={() => handleLanguageChange('en')}
          class="flex-1 py-3 px-4 rounded-lg font-medium transition-colors {getLocale() === 'en' ? 'bg-blue-600' : 'bg-gray-700'}"
        >
          English
        </button>
        <button
          onclick={() => handleLanguageChange('ko')}
          class="flex-1 py-3 px-4 rounded-lg font-medium transition-colors {getLocale() === 'ko' ? 'bg-blue-600' : 'bg-gray-700'}"
        >
          한국어
        </button>
      </div>
    </div>

    <!-- Paper size -->
    <div class="bg-gray-800 rounded-lg p-4">
      <h3 class="font-medium mb-2">{t('settings.paperSize')}</h3>
      <p class="text-sm text-gray-400 mb-3">{t('settings.paperSizeDesc')}</p>
      <div class="flex gap-2">
        <button
          onclick={() => handlePaperSizeChange('a4')}
          class="flex-1 py-3 px-4 rounded-lg font-medium transition-colors {$paperSize === 'a4' ? 'bg-blue-600' : 'bg-gray-700'}"
        >
          A4
        </button>
        <button
          onclick={() => handlePaperSizeChange('letter')}
          class="flex-1 py-3 px-4 rounded-lg font-medium transition-colors {$paperSize === 'letter' ? 'bg-blue-600' : 'bg-gray-700'}"
        >
          Letter
        </button>
      </div>
    </div>

    <!-- ML Engine -->
    <div class="bg-gray-800 rounded-lg p-4">
      <h3 class="font-medium mb-2">{t('settings.engine')}</h3>
      <p class="text-sm text-gray-400 mb-3">{t('settings.engineDesc')}</p>
      <div class="flex gap-2">
        <button
          onclick={() => handleEngineChange('auto')}
          disabled={changingEngine}
          class="flex-1 py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 {$executionProvider === 'auto' ? 'bg-blue-600' : 'bg-gray-700'}"
        >
          {t('settings.engineAuto')}
        </button>
        <button
          onclick={() => handleEngineChange('wasm')}
          disabled={changingEngine}
          class="flex-1 py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 {$executionProvider === 'wasm' ? 'bg-blue-600' : 'bg-gray-700'}"
        >
          {t('settings.engineWasm')}
        </button>
      </div>
      <p class="text-sm text-gray-500 mt-2">
        {#if inferenceStats.count > 0}
          {t('settings.inferenceTime', { time: inferenceStats.avgTime, count: inferenceStats.count })}
        {:else}
          {t('settings.inferenceTimeEmpty')}
        {/if}
      </p>
    </div>

    <!-- Report -->
    <div class="bg-gray-800 rounded-lg p-4">
      <h3 class="font-medium mb-2">{t('settings.report')}</h3>
      <p class="text-sm text-gray-400 mb-3">{t('settings.reportDesc')}</p>
      <button
        onclick={handleReport}
        class="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
      >
        {t('settings.reportButton')}
      </button>
    </div>

    <!-- Disconnect -->
    <div>
      <button
        onclick={handleDisconnect}
        class="w-full py-3 px-4 bg-red-600 hover:bg-red-500 rounded-lg font-medium transition-colors"
      >
        {t('settings.disconnect')}
      </button>
      <p class="text-sm text-gray-500 mt-2 text-center">
        {t('settings.disconnectDesc')}
      </p>
    </div>

    <!-- Version -->
    <p class="text-center text-sm text-gray-600">
      {t('settings.version', { version: WEB_VERSION })}
    </p>
  </main>
</div>

<style>
  .screen {
    display: flex;
  }
</style>
